import * as React from 'react';
import {defaults} from 'lodash';
import {styled} from '@packages/utilities/styled';
import OneLiner from './OneLiner';
import ButtonShell, {RealButtonShell} from './ButtonShell';
import Hitarea from './Hitarea';

// @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
const PassThrough = ({component = 'div', children, ...rest}) =>
  React.createElement(component, rest, children);

const alignments = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  baseline: 'baseline',
  stretch: 'stretch',
} as const;

const Cell: React.ComponentType<{
  ['aria-label']?: string;
  grow?: number;
  shrink?: number;
  align?: keyof typeof alignments;
  compact?: boolean;
  style?: Record<any, any>;
}> = styled(PassThrough)(
  ({grow, shrink, align, theme, 'aria-label': ariaLabel}) =>
    defaults({
      // Allows shrinking if necessary.
      minWidth: grow ? theme.control.height : undefined,
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly start: "flex-start"; readonly center: "center"; readonly end: "flex-end"; readonly baseline: "baseline"; readonly stretch: "stretch"; }'.
      alignSelf: alignments[align],
      flexGrow: grow,
      flexShrink: shrink,
      flexBasis: grow && 0,
      ariaLabel,
    })
);

Cell.displayName = 'LayoutCell';

const buttonItem = (compact: any) => (element: number) =>
  // The only falsy value we want to render is 0
  element || element === 0 ? (
    // @ts-expect-error - TS2322 - Type '{ children: number; component: AbstractComponent<Diff<PropsT, { style?: StyleObject; }>, any>; compact: any; }' is not assignable to type 'IntrinsicAttributes & { "aria-label"?: string | undefined; grow?: number | undefined; shrink?: number | undefined; align?: "start" | "end" | "center" | "baseline" | "stretch" | undefined; compact?: boolean | undefined; style?: Record<...> | undefined; } & { ...; }'.
    <Cell component={OneLiner} compact={compact}>
      {element}
    </Cell>
  ) : null;

type Props = any;

/**
 * @deprecated use `Button` from `@packages/systems/spring` instead
 */
export const Button = React.forwardRef<HTMLElement, Props>(
  (
    {compact, children, type = 'button', selected, segmented, ...props},
    ref
  ) => (
    <Hitarea>
      <RealButtonShell
        ref={ref}
        aria-checked={selected}
        type={type}
        role={segmented ? 'radio' : 'button'}
        data-legacy-ds="Button"
        {...props}
        segmented={segmented}
        selected={selected}
      >
        {React.Children.map(children, buttonItem(compact))}
      </RealButtonShell>
    </Hitarea>
  )
);

/**
 * Will be deprecating. Please use `Button` instead.
 *
 * @example
 * ```js
 * import Button from '@packages/systems/design-system/Button';
 * ```
 *
 * @see https://github.com/webflow/webflow/pull/43609
 */

/**
 * @deprecated use `Button` from `@packages/systems/spring` instead
 */
export const BaseButton = React.forwardRef<HTMLElement, Props>(
  ({compact, children, ...props}, ref) => (
    <ButtonShell ref={ref} data-legacy-ds="BaseButton" {...props}>
      {React.Children.map(children, buttonItem(compact))}
    </ButtonShell>
  )
);

/**
 * Deprecated. Please use `Button` instead.
 * This component will be removed soon.
 *
 * @example
 * ```js
 * import Button from '@packages/systems/design-system/Button';
 * ```
 *
 * @see https://github.com/webflow/webflow/pull/43609
 *
 * @deprecated use `Button` from `@packages/systems/spring` instead
 */
export const ButtonWithHitarea = Button;

/**
 * Deprecated. Please use `Button` instead.
 * This component will be removed soon.
 *
 * @example
 * ```js
 * import Button from '@packages/systems/design-system/Button';
 * ```
 *
 * @see https://github.com/webflow/webflow/pull/43609
 *
 * @deprecated use `Button` from `@packages/systems/spring` instead
 */
export const RealButton = Button;

/**
 * Deprecated. Please use `Button` instead.
 * This component will be removed soon.
 *
 * @example
 * ```js
 * import Button from '@packages/systems/design-system/Button';
 * ```
 *
 * @see https://github.com/webflow/webflow/pull/43609
 *
 * @deprecated use `Button` from `@packages/systems/spring` instead
 */
export const RealButtonWithHitarea = Button;

export default Button;
